import { GenericTable } from "@shared/components/GenericTable";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import { AlertTriangle, Clock, Shield } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useOutstandingBills, useOutstandingBillsExport } from "../../hooks";
import { OutstandingBill, OutstandingBillsFilters } from "../../types";

const renderRisk = (value: string | undefined) => {
  if (!value) return <span>N/A</span>;
  if (value === "High") {
    return (
      <span
        style={{
          color: "#e53e3e",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <AlertTriangle size={16} style={{ color: "#e53e3e" }} /> High
      </span>
    );
  }
  if (value === "Medium") {
    return (
      <span
        style={{
          color: "#d97706",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Clock size={16} style={{ color: "#d97706" }} /> Medium
      </span>
    );
  }
  if (value === "Low") {
    return (
      <span
        style={{
          color: "#2563eb",
          display: "flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <Shield size={16} style={{ color: "#2563eb" }} /> Low
      </span>
    );
  }
  return value;
};

const formatCurrency = (
  currency: string,
  value: string | number | null | undefined
) => {
  if (value === null || value === undefined || value === "") {
    return `${currency} NA`;
  }
  return `${currency} ${value}`;
};

// Define a table-specific type for the transformed data
interface OutstandingBillTableRow {
  id: string;
  accountNumber: string;
  remoteConsumerNumber: string;
  customerName: string;
  billedAmount: number;
  dueDate: string;
  outstandingAmount: string;
  lastPayment: number;
  lastPaymentDate: string | null;
  followUp: string;
  risk: string;
  daysPassed: any;
}

export const OutstandingCurrentList: React.FC = () => {
  const navigate = useNavigate();
  const currency = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const remoteUtilityId = useRemoteUtilityId();

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  // URL Params — single source of truth
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      searchData: currentSearch,
    }),
    [currentPage, currentSearch]
  );

  const { data } = useOutstandingBills({
    remoteUtilityId: Number(remoteUtilityId),
    page: queryParams.page,
    limit: PAGE_SIZE,
    searchData: queryParams.searchData,
  });

  const exportFilters: OutstandingBillsFilters = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      searchData: queryParams.searchData,
      // Note: No page and limit for export to get all data
    };
  }, [queryParams.searchData, remoteUtilityId]);

  const { exportCSV, isExporting } = useOutstandingBillsExport(exportFilters);

  const handleExport = () => {
    exportCSV();
  };

  const columns: TableColumn<OutstandingBillTableRow>[] = [
    { key: "accountNumber", header: "Account", type: "text" },
    {
      key: "billedAmount",
      header: "Billed Amount",
      type: "custom",
      render: (value) => formatCurrency(currency, value),
    },
    { key: "dueDate", header: "Due Date", type: "text" },
    {
      key: "outstandingAmount",
      header: "Outstanding",
      type: "custom",
      render: (value) => formatCurrency(currency, value),
    },
    {
      key: "daysPassed",
      header: "Days",
      type: "custom",
      render: (value) => value || "N/A",
    },
    {
      key: "lastPayment",
      header: "Last Payment",
      type: "custom",
      render: (_: any, row: any) => (
        <div>
          <div className="font-semibold">
            {formatCurrency(currency, row.lastPayment)}
          </div>
          <div className="text-xs text-muted-foreground">
            {row.lastPaymentDate ? row.lastPaymentDate : "N/A"}
          </div>
        </div>
      ),
    },
    { key: "followUp", header: "Follow up", type: "text" },
    {
      key: "risk",
      header: "Risk",
      type: "custom",
      render: (value) => renderRisk(value || ""),
    },
  ];

  const actions: TableAction<OutstandingBillTableRow>[] = [
    {
      label: "View Account",
      onClick: (item) =>
        navigate(`/cx/outstanding/${item.remoteConsumerNumber}`),
    },
  ];

  // Transform API data to match the table structure
  const transformedData: OutstandingBillTableRow[] =
    data?.result?.map((item: OutstandingBill) => ({
      remoteConsumerNumber: item.remoteConsumerNumber,
      id: item.id?.toString() || item.remoteConsumerNumber,
      accountNumber: item.remoteConsumerNumber,
      customerName: `${item.firstName || ""} ${item.lastName || ""}`.trim(),
      billedAmount: item.totalChargeExclusiveOfTax,
      dueDate: item.dueDate,
      outstandingAmount: item.outstandingBalance,
      daysPassed: item.daysPassed || "N/A",
      lastPayment: item.lastPayAmount,
      lastPaymentDate: item.lastPaymentDate,
      followUp: item.followUp,
      risk: item.risk,
    })) || [];

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };
    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search;
    }
    newParams.page = 1;
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  useEffect(() => {
    if (data) {
      const totalItems = data.count || 0;
      const totalPages = Math.ceil(totalItems / PAGE_SIZE);
      const startItem = (currentPage - 1) * PAGE_SIZE + 1;
      const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

      setPagination({
        currentPage: currentPage,
        totalPages,
        totalItems,
        itemsPerPage: PAGE_SIZE,
        startItem: totalItems > 0 ? startItem : 0,
        endItem: totalItems > 0 ? endItem : 0,
      });
    }
  }, [data, currentPage]);

  return (
    <GenericTable
      data={transformedData}
      columns={columns}
      rowKey="id"
      searchPlaceholder="Search by account number or customer name..."
      searchParamName="query"
      searchDebounceMs={300}
      onSearch={handleSearch}
      pagination={pagination}
      onPageChange={handlePageChange}
      actions={actions}
      actionsType="dropdown"
      onExport={handleExport} // Add this line
      exportLoading={isExporting} // Add this line
    />
  );
};
